<template>
  <div>
    <v-container       
      class="full-height"
      fluid
    >
      <v-card>
        <v-card-title>Attendance Summary By Employee Report (Mobile Attendance)</v-card-title>
        <v-row class="px-4">
          <v-col
            cols="8"
            class="pt-0"
          >
            <v-data-table 
              v-model="selectedEmployees"
              v-validate="'required'"
              dense
              :error-messages="errors.collect('selectedEmployees')"
              data-vv-name="selectedEmployees"
              data-vv-as="employees"
              :headers="headers"
              :items="filteredEmployees"
              hide-default-footer
              fixed-header
              disable-pagination
              show-select
              item-key="employee_uid"
              item-value="employee_uid"
              class="elevation-2 mb-3"
              height="420px"
            >
              <template v-slot:top>
                <h5 class="font-weight-bold py-2 mb-0 pl-2">
                  Employee Selection:
                </h5>
              </template>
            </v-data-table>
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>                  
          </v-col>

          <v-col
            cols="4"
            class="pt-0"
          >
            <v-text-field
              v-model="searchInput"
              placeholder="Filter employee by name"
              append-icon="search"
              label="Search Employee"
              hide-no-data
              hide-selected
            />

            <v-autocomplete
              v-model="filters.companies"
              :items="companies"
              item-text="name"
              item-value="id"
              label="Company"
              placeholder="Filter employee by company"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.departments"
              :items="departments"
              item-text="name"
              item-value="id"
              value="id"
              label="Department"
              placeholder="Filter employee by department"
              clearable
              hide-selected
              multiple
              deletable-chips
              small-chips
              chips
            />

            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDateDisplay"
                  v-validate="'required'"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  :error-messages="errors.collect('startDateDisplay')"
                  data-vv-name="startDateDisplay"  
                  data-vv-as="start date"    
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                :max="endDate"
                @input="startDateMenu = false"
              />
            </v-menu>

            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endDateDisplay"
                  v-validate="'required'"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  :error-messages="errors.collect('endDateDisplay')"
                  data-vv-name="endDateDisplay"  
                  data-vv-as="end date"  
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                :min="startDate"
                @input="endDateMenu = false"
              />
            </v-menu>

            <v-card-actions>
              <v-spacer />
              <v-btn
                block
                :loading="loading"
                color="hrissecondary"     
                class="white--text"     
                @click="download"
              >
                Generate
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>  
      </v-card>
    </v-container>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employees: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        startDateMenu: false,
        startDate: this.$moment().startOf('year').format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),
        loading: false,
        employee: [],
        searchInput: '',
        filters: {
          companies: '',
          departments: '',
        },
        selectedCompanies: [],
        selectedEmployees: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'department.name',
          },
          {
            text: 'Resigned Date',
            align: 'left',
            sortable: true,
            value: 'end_date',
          },
        ],
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1 
        })
        
        let secondaryFilterEmployees = primaryFilterEmployees.filter(function (employee) {
          return ((!employee.end_date || employee.end_date > _this.endDate) || (employee.end_date && employee.end_date >= _this.startDate && employee.end_date <= _this.endDate))
        })

        let filteredCompanies = this.customFilterCompanies(secondaryFilterEmployees)
        let filteredDepartments = this.customFilterDepartments(filteredCompanies)
        return filteredDepartments
      },
    },
    watch: {
      filteredEmployees (newList) {
        this.$validator.pause()

        const newEmployeeUids = new Set(newList.map(emp => emp.employee_uid))

        this.selectedEmployees = this.selectedEmployees.filter(selectedEmp =>
          newEmployeeUids.has(selectedEmp.employee_uid)
        )

        this.$nextTick(() => {
          this.$validator.resume()
        })
      },
    },
    methods: {
      download () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.attendanceReports().generateAttendanceSummaryReport(this.prepareFormData())
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Attendance Summary By Employee Report.xlsx') 
                link.click()
                this.loading = false
              }).catch((e) => {
                this.loading = false
              })
          })
      },

      prepareFormData () {
        return {
          employeeUids: this.getEmployeeUids(),
          startDate: this.startDate,
          endDate: this.endDate, 
        }
      },
      
      getEmployeeUids () {
        let employeeUids = []

        this.selectedEmployees.map(item => {
          employeeUids.push(item.employee_uid)
        })

        return employeeUids
      },

      customFilterCompanies (employeesList) {
        if (this.filters.companies.length === 0) return employeesList

        return employeesList.filter(employee => {
          if (employee.company_employees.length <= 0) return 

          let companyEmployees = employee.company_employees.filter(companyEmployee => {
            return (companyEmployee.is_active_pay_status === 1 && companyEmployee.start_date <= this.endDate && (!companyEmployee.end_date || companyEmployee.end_date >= this.startDate)) || (companyEmployee.end_date && companyEmployee.end_date >= this.startDate && companyEmployee.end_date <= this.endDate)
          })

          if (companyEmployees.length > 0) {
            let latestPayrollCompany = companyEmployees[0].company_uid
  
            return _.intersection([latestPayrollCompany], this.filters.companies).length > 0
          }
        })
      },

      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },
    },
  }
</script>
